/**
 * Saisons de volley — convention "YYYY/YYYY+1" (ex. "2026/2027").
 * Miroir de `Volley_Stats_Backend/src/common/utils/season.util.ts`.
 *
 * La saison sportive bascule au 1er août : à partir de ce mois, on considère
 * qu'on est déjà dans la nouvelle saison.
 */

/** Mois (0-indexé) à partir duquel on bascule sur la nouvelle saison. 7 = août. */
export const SEASON_START_MONTH = 7;

/** Saison courante déduite d'une date (défaut : maintenant). */
export function getCurrentSeason(date: Date = new Date()): string {
  const year = date.getFullYear();
  const startYear = date.getMonth() >= SEASON_START_MONTH ? year : year - 1;
  return `${startYear}/${startYear + 1}`;
}

/**
 * Current season and the next one: the seasons a coach still prepares or plays,
 * the same window as the plan quota of active teams (VOL-195).
 */
export function getCurrentAndNextSeason(date: Date = new Date()): [string, string] {
  const current = getCurrentSeason(date);
  const startYear = Number(current.slice(0, 4)) + 1;
  return [current, `${startYear}/${startYear + 1}`];
}

/** Season before `season` ("2027/2028" → "2026/2027"). */
export function getPreviousSeason(season: string): string {
  const startYear = Number(season.slice(0, 4)) - 1;
  return `${startYear}/${startYear + 1}`;
}

const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Heure d'ancrage (midi local) — voir `getSeasonForIsoDate`. */
const NOON = 12;

/**
 * Saison d'un match déduite de sa date ISO `YYYY-MM-DD`.
 *
 * Miroir exact de `getSeasonForIsoDate` côté backend. La date est ancrée à **midi
 * local** plutôt que construite via `new Date(iso)` (minuit UTC), qui ferait
 * basculer un match du 1er août sur le 31 juillet — donc sur la saison précédente
 * — pour tout fuseau à l'ouest de Greenwich.
 *
 * Retombe sur la saison courante si la date est absente ou illisible.
 */
export function getSeasonForIsoDate(isoDate?: string): string {
  const match = isoDate ? ISO_DATE_PATTERN.exec(isoDate.trim()) : null;
  if (!match) return getCurrentSeason();
  return getCurrentSeason(
    new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), NOON),
  );
}

/** Date du jour au format ISO `YYYY-MM-DD`, en **heure locale**.
 *
 * `new Date().toISOString()` renvoie la date UTC : après 22 h (heure d'été
 * française), elle désigne déjà le lendemain — et avant 2 h du matin, la veille.
 * Une saisie terminée après un match du samedi soir se voyait ainsi proposer la
 * mauvaise date.
 */
export function todayIsoDate(date: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Libellé court d'une saison, ex. "2026/2027" → "26/27". */
export function shortSeasonLabel(season: string): string {
  const match = /^(\d{2})(\d{2})\/(\d{2})(\d{2})$/.exec(season);
  if (!match) return season;
  return `${match[2]}/${match[4]}`;
}
